import * as React from 'react';
import {
  Breadcrumb as ShadcnBreadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/hen-ui/shadcn/breadcrumb';
import { cn } from '@/hen-ui/utils';
import { Link, Text } from '@/hen-ui/atomics';

export interface BreadcrumbProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnBreadcrumb> {
  items: {
    label: string;
    href?: string;
  }[];
  maxItems?: number;
  color?: 'default' | 'white';
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, maxItems = 3, color = 'default', className, ...props }, ref) => {
    if (maxItems < 1) {
      throw new Error('Breadcrumb: maxItems must be at least 1');
    }

    const numOfItemsToHide = Math.max(0, (items?.length ?? 0) - maxItems);

    const firstItem = items?.[0];
    const lastItem = items?.[items?.length - 1];
    const hasMultipleItems = (items?.length ?? 0) > 1;

    return (
      <ShadcnBreadcrumb
        className={cn(
          'min-h-d2',
          color === 'white' && '[&_*]:text-white [&_svg]:stroke-white',
          className,
        )}
        ref={ref}
        {...props}
      >
        <BreadcrumbList className='!gap-xxs'>
          {hasMultipleItems && maxItems > 1 && (
            <>
              <BreadcrumbItem>
                {firstItem?.href ? (
                  <BreadcrumbLink asChild>
                    <Link href={firstItem.href}>
                      <Text variant='b5'>{firstItem.label}</Text>
                    </Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>
                    <Text variant='b5'>{firstItem?.label}</Text>
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbEllipsis />
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </>
          )}
          {items
            ?.slice(1 + numOfItemsToHide, items?.length - 1)
            ?.map(({ label, href }, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  {href ? (
                    <BreadcrumbLink asChild>
                      <Link href={href}>
                        <Text variant='b5'>{label}</Text>
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>
                      <Text variant='b5'>{label}</Text>
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </React.Fragment>
            ))}
          <BreadcrumbItem>
            <BreadcrumbPage>
              <Text variant='b5'>{lastItem?.label}</Text>
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </ShadcnBreadcrumb>
    );
  },
);
